'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinnerThird } from '@awesome.me/kit-94e593bf23/icons/classic/light'
import { useReCaptcha } from 'next-recaptcha-v3'

import RecaptchaDisclaimer from '@/app/_components/RecaptchaDisclaimer'
import FormError from '@/app/_components/FormError'
import sendPasswordResetCodeAction from '@/app/_lib/actions/SendPasswordResetCodeAction'
import { setResetEmail, clearResetState } from './resetPasswordStorage'

const emailSchema = z.object({
    email: z.string().min(1, 'Email is required').email('Please enter a valid email'),
})

/**
 * EmailStep - Step 1 of password reset flow
 *
 * User enters email address, we send a 6-digit code,
 * then redirect to /verify step.
 */
const EmailStep = () => {
    const router = useRouter()
    const { executeRecaptcha } = useReCaptcha()
    const [serverError, setServerError] = useState('')
    const [noAccount, setNoAccount] = useState(false)

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting }
    } = useForm({
        resolver: zodResolver(emailSchema),
        defaultValues: {
            email: '',
        }
    })

    const onSubmit = async (data) => {
        setServerError('')
        setNoAccount(false)

        // Clear any stale state from previous attempts
        clearResetState()

        // Get reCAPTCHA token
        const recaptchaToken = await executeRecaptcha('password_reset')
        if (!recaptchaToken) {
            setServerError('Security verification failed. Please try again.')
            return
        }

        const response = await sendPasswordResetCodeAction({ email: data.email, recaptchaToken })

        if (response.success) {
            // Store email for next step
            setResetEmail(data.email, response.maskedEmail)
            // Redirect to code entry
            router.push('/account/reset-password/verify')
        } else {
            setNoAccount(!! response.noAccount)
            setServerError(response.message || 'Something went wrong. Please try again.')
        }
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <p className="text-sm text-gray-600 mb-4">
                Enter your email address and we&apos;ll send you a code to reset your password.
            </p>

            {serverError && (
                <div
                    role="alert"
                    aria-live="assertive"
                    className="text-red text-sm mb-4 p-3 bg-red-50 rounded"
                >
                    {noAccount ? (
                        <>
                            Please provide us with your account&apos;s verified email address to reset your
                            password. If you do not have an account you can create one here{' '}
                            <Link href="/account/new" className="underline underline-offset-2">
                                create account
                            </Link>
                        </>
                    ) : serverError}
                </div>
            )}

            {errors.email && (
                <FormError id="email-error">{errors.email.message}</FormError>
            )}
            <div className="form-group mb-4">
                <input
                    id="reset-email"
                    type="email"
                    autoComplete="email"
                    placeholder=" "
                    className={`form-input peer ${errors.email ? 'border-red' : ''}`}
                    disabled={isSubmitting}
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? "email-error" : undefined}
                    {...register('email')}
                />
                <label
                    htmlFor="reset-email"
                    className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown"
                >
                    Email <span className="text-primary">*</span>
                </label>
            </div>

            <div className="flex items-center gap-4">
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`btn btn-primary w-auto flex items-center justify-center ${isSubmitting ? 'opacity-75' : ''}`}
                >
                    {isSubmitting ? (
                        <>
                            <FontAwesomeIcon icon={faSpinnerThird} className="animate-spin mr-2" />
                            Sending...
                        </>
                    ) : (
                        'Send Code'
                    )}
                </button>
                <Link
                    href="/account/login"
                    className="text-xs text-gray-400 hover:text-gray-600 transition-colors underline"
                >
                    Cancel
                </Link>
            </div>

            <div className="mt-4">
                <RecaptchaDisclaimer />
            </div>

            <p className="text-xs text-gray-500 mt-6 pt-4 border-t border-gray-200">
                Already have a code?{' '}
                <Link
                    href="/account/reset-password/verify"
                    className="text-primary hover:underline"
                >
                    Enter it here
                </Link>
            </p>
        </form>
    )
}

export default EmailStep
